/* eslint-disable */
import type { LocalizableString } from "../../cash/localization/localization.pb";
import type { Color } from "../../cash/ui/Color.pb";
import type { Image } from "../../cash/ui/Image.pb";
import type { Country } from "../../common/countries.pb";
import type { LoyaltyUnit, PointsReward } from "../loyalty.pb";
import type { Region } from "../region.pb";
import type { UiCheckAddress } from "./check_address.pb";

/**
 * State that tells if the customer has been blocked by the requesting customer.
 * If it's a special customer like transfer customer it's state is NOT_APPLICABLE.
 */
export enum BlockState {
  NOT_BLOCKED = "NOT_BLOCKED",
  BLOCKED = "BLOCKED",
  NOT_APPLICABLE = "NOT_APPLICABLE",
}

/** A client's view of a customer. */
export interface UiCustomer {
  /**
   * An opaque token used to identify this customer. In practice, the Franklin customer token.
   * If this is a merchant, it represents a merchantine token.
   */
  id?: string;
  /**
   * Used for grouping transactions in a threaded view. If present, use this grouping token over
   * any other grouping tokens.
   */
  threaded_customer_id?: string;
  email_address?: string;
  full_name?: string;
  sms_number?: string;
  /**
   * DEPRRECATED in favor of photo (iOS/Android 3.34).
   * This must remain populated for older clients.
   *
   * @deprecated
   */
  photo_url?: string;
  /** If true, the customer has linked a payment instrument and should be shown as a Cash User. */
  is_cash_customer?: boolean;
  /**
   * If false, the customer cannot accept cash or requests. The apps shouldn't show them in the
   * contact picker. Example: demo@square.com
   */
  can_accept_payments?: boolean;
  /**
   * If true, the customer is a special Square customer. The app can show an "Invite Friends"
   * button on the activity screen for these customers.
   */
  is_square?: boolean;
  cashtag?: string;
  /** If true, this customer is nearby and was discovered by BLE. */
  is_nearby?: boolean;
  /** If true, this customer is a business (Cash pro) customer. */
  is_business?: boolean;
  /** If true, the customer's display name was verified via Twitter or Facebook or manually by us. */
  is_verified_account?: boolean;
  selection_method?: UiCustomerSelectionMethod;
  check_address?: UiCheckAddress;
  /**
   * Fee to use when paying this customer by credit card. If null, use the default.
   * This allows for non-standard credit card rates for customers in specific states.
   */
  credit_card_fee_bps?: number;
  /**
   * An opaque string to the client. The render data is passed into javascript when generating the
   * payment history data client-side.
   */
  render_data?: string;
  block_state?: BlockState;
  /**
   * If the customer represents a merchant this field will be non-null and contains merchant
   * specific data.
   */
  merchant_data?: MerchantData;
  /**
   * Accent color 6 digit hex with leading # sign (ex: "#BBBFB3") for the image placeholder
   * circle/initials, and potentially other UI elements associated with the customer.
   * DEPRECATED in favor of themed_accent_color (iOS/Android 3.34).
   * This must remain populated for older clients.
   *
   * @deprecated
   */
  accent_color?: string;
  /**
   * The customer's country. Deprecated in favour of region.
   *
   * Reason for deprecation: The code that sets this makes the false assumption that a region
   * only has one country.
   *
   * @deprecated
   */
  country_code?: Country;
  /**
   * If the customer represents an investment, this field will be non-null and contain
   * investment specific data.
   * Client uses this to map between a customer & sync investment entity.
   * Removing it would have side effects like:
   *  - investing "customers" would show up as recipient suggestions
   *  - not showing gifts from the list of payments in the threaded feed
   *  - showing the wrong style for the threaded feed (it'd try to show a regular customer profile instead)
   *  - not allowing cancelling recurring buys
   */
  investment_entity_data?: InvestmentEntityData;
  /** The customer's region */
  region?: Region;
  /** The date this customer joined Cash in milliseconds since 1/1/1970 UTC. */
  customer_joined_on?: number;
  /** Image URL(s) for customer photo. Contains two variants for investing and merchant "customers" with dark-themed icons. */
  photo?: Image;
  /**
   * Accent colors: 6 digit hex with leading # sign (ex: "#BBBFB3") for the image placeholder
   * circle/initials, and potentially other UI elements associated with the customer.
   */
  themed_accent_color?: Color;
  /**
   * Represents the suspension state of the customer
   * TODO(aparmar): This field is not yet populated as of 10/20/2022
   */
  is_suspended?: boolean;
  /**
   * If set, the client should follow this url (likely a client route) to send money to this customer.
   * Used by Remittances for international non-Cash recipients
   */
  payment_url?: string;
}

/** The method by which this customer was selected, when initiating a payment to this customer. */
export enum UiCustomerSelectionMethod {
  NEARBY = "NEARBY",
  RECENT = "RECENT",
  CONTACT = "CONTACT",
  SEARCH = "SEARCH",
  ACTIVITY = "ACTIVITY",
}

export interface MerchantData {
  category?: string;
  should_colorize_avatar?: boolean;
  /**
   * This flag is deprecated in favor of should_fill_background. Only old apps still use this flag.
   *
   * @deprecated
   */
  should_draw_avatar_border?: boolean;
  should_fill_background?: boolean;
  /**
   * A list of rewards available at this merchant.
   *
   * @deprecated
   */
  rewards?: PointsReward[];
  /**
   * Terminology for the loyalty program unit, e.g. stars/points/punches.
   *
   * @deprecated
   */
  loyalty_unit?: LoyaltyUnit;
  /**
   * A description that explains how to obtain loyalty points.
   *
   * @deprecated
   */
  program_description?: string;
  /**
   * A unique identifier for a cash parent merchant that's associated with loyalty merchant.
   *
   * @deprecated
   */
  cash_token?: string;
  /** In app browser route to open affiliate link for Afterpay merchants. */
  in_app_browser_shop_url?: string;
  localizable_category?: LocalizableString;
}

/** UiCustomer field for investment enrichment data. */
export interface InvestmentEntityData {
  /** Token associated with investment entity for trade for UiCustomer. */
  investment_entity_token?: string;
}
